import { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Building2, Phone, Mail, User } from 'lucide-react';
import { useLabClinics, useCreateLabClinic, useUpdateLabClinic, useDeleteLabClinic } from '@/hooks/useLab';
import { LabClinic } from '@/types';
import { LabClinicCreate } from '@/services/lab';

export default function LabClinics() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState<LabClinic | null>(null);
  const [deletingClinic, setDeletingClinic] = useState<LabClinic | null>(null);
  const [formData, setFormData] = useState<LabClinicCreate>({
    name: '',
    address: '',
    phone: '',
    email: '',
    contact_vet_name: '',
    contact_vet_phone: '',
    notes: '',
  });

  const { data: clinics = [], isLoading, refetch } = useLabClinics({
    search: search || undefined,
    is_active: true,
  });
  const createMutation = useCreateLabClinic();
  const updateMutation = useUpdateLabClinic();
  const deleteMutation = useDeleteLabClinic();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClinic) {
        await updateMutation.mutateAsync({
          id: editingClinic.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (clinic: LabClinic) => {
    setEditingClinic(clinic);
    setFormData({
      name: clinic.name,
      address: clinic.address || '',
      phone: clinic.phone || '',
      email: clinic.email || '',
      contact_vet_name: clinic.contact_vet_name || '',
      contact_vet_phone: clinic.contact_vet_phone || '',
      notes: clinic.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deletingClinic) return;
    await deleteMutation.mutateAsync(deletingClinic.id);
    setDeletingClinic(null);
    refetch();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClinic(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      contact_vet_name: '',
      contact_vet_phone: '',
      notes: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clinicas Clientes</h1>
          <p className="text-gray-600">Gestionar veterinarias que derivan pacientes</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-lab-600 text-white px-4 py-2 rounded-lg hover:bg-lab-700"
        >
          <Plus className="h-5 w-5" />
          Nueva Clinica
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o veterinario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : clinics.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hay clinicas registradas</p>
            <p className="text-sm mt-2">Agregue clinicas que derivan pacientes al laboratorio</p>
          </div>
        ) : (
          <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
            {clinics.map((clinic) => (
              <div
                key={clinic.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-lab-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-lab-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">{clinic.name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(clinic)}
                      className="p-1.5 text-lab-600 hover:bg-lab-50 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingClinic(clinic)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  {clinic.address && (
                    <p className="truncate">{clinic.address}</p>
                  )}
                  {clinic.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{clinic.phone}</span>
                    </div>
                  )}
                  {clinic.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{clinic.email}</span>
                    </div>
                  )}
                  {clinic.contact_vet_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{clinic.contact_vet_name}</span>
                      {clinic.contact_vet_phone && (
                        <span className="text-gray-400">({clinic.contact_vet_phone})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingClinic && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Clínica</h3>
              <p className="text-gray-600">
                ¿Estás seguro de que querés eliminar <span className="font-semibold">{deletingClinic.name}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setDeletingClinic(null)}
                className="flex-1 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingClinic ? 'Editar Clinica' : 'Nueva Clinica'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Nombre de la clinica"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Direccion"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="Telefono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="email@clinica.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Veterinario de contacto</label>
                  <input
                    type="text"
                    value={formData.contact_vet_name}
                    onChange={(e) => setFormData({ ...formData, contact_vet_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="Dr. ..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tel. veterinario</label>
                  <input
                    type="text"
                    value={formData.contact_vet_phone}
                    onChange={(e) => setFormData({ ...formData, contact_vet_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="Telefono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-lab-600 text-white rounded-lg hover:bg-lab-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
